import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { MixEngine } from "../../lib/mixEngine";
import { Instruments } from "../../lib/instruments";
import { errMsg, mediaUrl } from "../../lib/api";

const NO_KEYS = "Live video isn't connected: the camp's LiveKit keys aren't valid yet.";

// One audio engine + instrument rack per space (SpaceView is keyed by space id).
export function useAudioRig() {
  const [engine] = useState(() => new MixEngine());
  const [inst] = useState(() => new Instruments(engine.ctx));
  useEffect(() => {
    engine.connectOut();
    return () => engine.dispose();
  }, [engine]);
  return { engine, inst };
}

// Fetches a LiveKit token whenever stage status changes (publish rights live in the token).
export function useLiveToken(api, spaceId, state) {
  const [lk, setLk] = useState(null);
  const [lkError, setLkError] = useState("");
  const loaded = !!state;
  const onStage = state?.me.on_stage;
  const livekit = state?.livekit;
  useEffect(() => {
    if (!loaded) return undefined;
    if (!livekit) { setLk(null); setLkError(NO_KEYS); return undefined; }
    let off = false;
    api.get(`/spaces/${spaceId}/token`)
      .then((r) => { if (!off) { setLk(r.data); setLkError(""); } })
      .catch((e) => { if (!off) setLkError(errMsg(e, "Couldn't connect live video")); });
    return () => { off = true; };
  }, [api, spaceId, onStage, livekit, loaded]);
  return { lk, lkError };
}

// Keeps the local mix in step with the host's mix; hosts' own edits win for 2.5s and are saved with a debounce.
export function useMixSync(api, spaceId, engine, serverMixer) {
  const [mix, setMix] = useState(null);
  const lastEdit = useRef(0);
  const saveT = useRef(null);
  useEffect(() => { if (serverMixer && Date.now() - lastEdit.current > 2500) setMix(serverMixer); }, [serverMixer]);
  useEffect(() => { if (mix) engine.apply(mix); }, [mix, engine]);
  useEffect(() => () => clearTimeout(saveT.current), []);
  const changeMix = useCallback((next) => {
    lastEdit.current = Date.now();
    setMix(next);
    clearTimeout(saveT.current);
    saveT.current = setTimeout(() => api.patch(`/spaces/${spaceId}/mixer`, next).catch((e) => toast.error(errMsg(e))), 300);
  }, [api, spaceId]);
  return { mix, changeMix };
}

// Plays the room's shared track in sync with the host's "Play to room".
export function usePlaybackSync(api, engine, playback) {
  const urls = useRef({});
  const trackId = playback?.track_id;
  const playing = !!playback?.playing;
  const startedAt = playback?.started_at;
  useEffect(() => {
    if (!trackId) { engine.player.pause(); return undefined; }
    let off = false;
    (async () => {
      try {
        urls.current[trackId] ||= await mediaUrl(api, trackId);
        if (off) return;
        engine.setPlayerTrack(`t:${trackId}`, urls.current[trackId]);
        if (!playing) { engine.player.pause(); return; }
        const offset = (Date.now() - Date.parse(startedAt)) / 1000;
        if (Math.abs(engine.player.currentTime - offset) > 1.5) engine.player.currentTime = Math.max(0, offset);
        await engine.player.play();
      } catch (e) {
        if (["NotAllowedError", "AbortError"].includes(e?.name)) return; // autoplay blocked until "Turn on sound" / src switched
        toast.error("This track couldn't be played here");
      }
    })();
    return () => { off = true; };
  }, [api, engine, trackId, playing, startedAt]);
}

// Routes this user's instrument rack into the local mixer only while they're allowed to play.
export function useInstrumentRouting(engine, inst, myId, canPlay) {
  useEffect(() => {
    if (!myId) return;
    if (canPlay) engine.addNode(`i:${myId}`, inst.local);
    else { inst.allOff(); engine.removeSource(`i:${myId}`); }
  }, [canPlay, myId, engine, inst]);
}

export const mixOptions = (state, mix, tracks) => {
  const { people, me, kind } = state;
  const known = new Set(people.flatMap((p) => [`p:${p.user_id}`, `i:${p.user_id}`]));
  tracks.forEach((t) => known.add(`t:${t.id}`));
  const players = kind === "lesson" ? people : people.filter((p) => p.on_stage || p.user_id === me.user_id);
  const opts = [
    ...people.filter((p) => p.on_stage).map((p) => ({ key: `p:${p.user_id}`, label: `Mic · ${p.name}` })),
    ...players.map((p) => ({ key: `i:${p.user_id}`, label: `Instruments · ${p.name}` })),
    ...tracks.map((t) => ({ key: `t:${t.id}`, label: `Track · ${t.title}` })),
    ...mix.channels.filter((c) => c.source && !known.has(c.source)).map((c) => ({ key: c.source, label: "Away" })),
  ];
  return opts.filter((o, i, a) => a.findIndex((x) => x.key === o.key) === i);
};
